<script setup>
import ImportPlanningKeywordsAction from '@frontJs/components/actions/projectManagement/sea/ImportPlanningKeywordsAction.vue';
import PencilSimpleIcon from '@iconify-vue/ph/pencil-simple';
import PlusIcon from '@iconify-vue/ph/plus';
import UploadSimpleIcon from '@iconify-vue/ph/upload-simple';
import DisplayLineChartAction from '@js/components/actions/DisplayLineChartAction.vue';
import ActionButton from '@js/components/buttons/ActionButton.vue';
import DataTableLayout from '@js/components/tables/DataTableLayout.vue';
import BaseCell from '@js/components/tables/cells/BaseCell.vue';
import HeaderCell from '@js/components/tables/cells/HeaderCell.vue';
import NumberCell from '@js/components/tables/cells/NumberCell.vue';
import PillCell from '@js/components/tables/cells/PillCell.vue';
import PriceCell from '@js/components/tables/cells/PriceCell.vue';
import StatusCell from '@js/components/tables/cells/StatusCell.vue';
import TextCell from '@js/components/tables/cells/TextCell.vue';
import { useDataTable } from '@js/utils/dataTable';
import { useRoute } from '@js/utils/route';
import { createColumnHelper } from '@tanstack/vue-table';
import { h } from 'vue';

const {
    tenant,
    googleAdsPlanningKeywordCountries,
    googleAdsPlanningKeywordLanguages,
    googleAdsPlanningKeywordPlanNetworks,
} = defineProps({
    tenant: {
        type: Object,
        required: true,
    },
    googleAdsPlanningKeywordCountries: {
        type: Object,
        required: true,
    },
    googleAdsPlanningKeywordLanguages: {
        type: Array,
        required: true,
    },
    googleAdsPlanningKeywordPlanNetworks: {
        type: Array,
        required: true,
    },
});

const { route } = useRoute();

const columnHelper = createColumnHelper();

const {
    loading,
    table,
    count,
    globalFilter,
    filters,
    availableFilters,
    setGlobalFilter,
    setFilters,
    updateData,
} = useDataTable({
    columns: [
        columnHelper.accessor(() => null, {
            id: 'keyword',
            header: () => 'Mot-clé',
            cell: (props) =>
                h(TextCell, {
                    text: props.row.original.keyword,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'country',
            header: () => 'Pays',
            cell: (props) =>
                props.row.original.country
                    ? h(PillCell, {
                          pillValue: props.row.original.country.label,
                      })
                    : null,
        }),
        columnHelper.accessor(() => null, {
            id: 'language',
            header: () => 'Langue',
            cell: (props) =>
                props.row.original.language
                    ? h(PillCell, {
                          pillValue: props.row.original.language.label,
                      })
                    : null,
        }),
        columnHelper.accessor(() => null, {
            id: 'planNetwork',
            header: () => 'Réseau',
            cell: (props) =>
                h(PillCell, {
                    pillValue: props.row.original.planNetwork.label,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'includeAdult',
            header: () => 'Inclure les contenus pour adultes',
            cell: (props) =>
                h(StatusCell, {
                    status: props.row.original.includeAdult,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'averageMonthlySearches',
            header: () => 'Nb moyen recherches mensuelles',
            cell: (props) =>
                props.row.original.googleAdsPlanningKeywordMetric
                    ? h(
                          NumberCell,
                          {
                              number: props.row.original
                                  .googleAdsPlanningKeywordMetric
                                  .averageMonthlySearches,
                              align: 'right',
                          },
                          {
                              actions: () =>
                                  h(DisplayLineChartAction, {
                                      labels: props.row.original
                                          .googleAdsPlanningKeywordMonthlySearchVolumeChartData
                                          .labels,
                                      datasets: [
                                          {
                                              group: 'Volume',
                                              label: 'Volume',
                                              backgroundColor: '#4d65b9',
                                              borderColor: '#4d65b9',
                                              pointRadius: 0,
                                              pointHoverRadius: 0,
                                              fill: {
                                                  target: 'origin',
                                                  above: '#e4e8f4',
                                              },
                                              tension: 0.4,
                                              data: props.row.original
                                                  .googleAdsPlanningKeywordMonthlySearchVolumeChartData
                                                  .data,
                                              labels: props.row.original
                                                  .googleAdsPlanningKeywordMonthlySearchVolumeChartData
                                                  .labels,
                                              yAxisID: 'y',
                                          },
                                      ],
                                      title: 'Recherches mensuelles moyennes',
                                      withDecimalsAxis1: false,
                                      axis1BeginAtZero: true,
                                  }),
                          },
                      )
                    : null,
        }),
        columnHelper.accessor(() => null, {
            id: 'competitionLevel',
            header: () => 'Concurrence',
            cell: (props) =>
                props.row.original.googleAdsPlanningKeywordMetric
                    ? h(PillCell, {
                          pillValue:
                              props.row.original.googleAdsPlanningKeywordMetric
                                  .competitionLevelLabel,
                          type:
                              props.row.original.googleAdsPlanningKeywordMetric
                                  .competitionLevel === 2
                                  ? 'danger'
                                  : props.row.original
                                          .googleAdsPlanningKeywordMetric
                                          .competitionLevel === 3
                                    ? 'warning'
                                    : props.row.original
                                            .googleAdsPlanningKeywordMetric
                                            .competitionLevel === 4
                                      ? 'success'
                                      : 'primary',
                      })
                    : null,
        }),
        columnHelper.accessor(() => null, {
            id: 'lowTopOfPageBidMicros',
            header: () => 'Enchère haut de page (fourchette basse)',
            cell: (props) =>
                props.row.original.googleAdsPlanningKeywordMetric
                    ? h(PriceCell, {
                          price: props.row.original
                              .googleAdsPlanningKeywordMetric.lowTopOfPageBid,
                      })
                    : null,
        }),
        columnHelper.accessor(() => null, {
            id: 'highTopOfPageBidMicros',
            header: () => 'Enchère haut de page (fourchette haute)',
            cell: (props) =>
                props.row.original.googleAdsPlanningKeywordMetric
                    ? h(PriceCell, {
                          price: props.row.original
                              .googleAdsPlanningKeywordMetric.highTopOfPageBid,
                      })
                    : null,
        }),
        columnHelper.accessor(() => null, {
            id: 'averageCpcMicros',
            header: () => 'CPC moyen',
            cell: (props) =>
                props.row.original.googleAdsPlanningKeywordMetric
                    ? h(PriceCell, {
                          price: props.row.original
                              .googleAdsPlanningKeywordMetric.averageCpc,
                      })
                    : null,
        }),
        columnHelper.display({
            id: 'actions',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Actions',
                    width: 0,
                }),
            cell: (props) =>
                h(
                    BaseCell,
                    {},
                    {
                        default: () =>
                            h(ActionButton, {
                                size: 'small',
                                square: true,
                                title: 'Modifier',
                                href: route(
                                    'front.projectManagement.sea.planningKeyword',
                                    {
                                        tenant: tenant.id,
                                        googleAdsPlanningKeywordId:
                                            props.row.original.id,
                                    },
                                ),
                                icon: PencilSimpleIcon,
                            }),
                    },
                ),
        }),
    ],
    url: route('front.projectManagement.sea.postPlanningKeywords', {
        tenant: tenant.id,
    }),
    columnPinning: {
        right: ['actions'],
    },
});
</script>

<template>
    <DataTableLayout
        :table="table"
        :count="count"
        :global-filter="globalFilter"
        :filters="filters"
        :available-filters="availableFilters"
        :set-filters="setFilters"
        :set-global-filter="setGlobalFilter"
        :loading="loading"
        force-scroll
    >
        <template #actions>
            <ImportPlanningKeywordsAction
                :tenant="tenant"
                :google-ads-planning-keyword-countries="
                    googleAdsPlanningKeywordCountries
                "
                :google-ads-planning-keyword-languages="
                    googleAdsPlanningKeywordLanguages
                "
                :google-ads-planning-keyword-plan-networks="
                    googleAdsPlanningKeywordPlanNetworks
                "
                :callback="updateData"
            >
                <template #trigger>
                    <ActionButton
                        size="small"
                        :left-icon="UploadSimpleIcon"
                        type="primary"
                    >
                        Importer
                    </ActionButton>
                </template>
            </ImportPlanningKeywordsAction>

            <ActionButton
                size="small"
                :href="
                    route('front.projectManagement.sea.planningKeyword', {
                        tenant: tenant.id,
                    })
                "
                :left-icon="PlusIcon"
            >
                Ajouter
            </ActionButton>
        </template>
    </DataTableLayout>
</template>
